import React from 'react';
import { AccountClient } from '@nile/graphql';
import { GET_AI_AGENT_NOTIFICATIONS } from '@nile/banyan/src/Graphql/Query/ai';
import { AI_AGENTS_MAP, useIsAuthorisedHook } from '@nile/components';
import { isEmpty } from 'lodash';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { useAINotificationStore } from '../store/aiNotificationStore';

const MOCK = {
  data: {
    getAIAgentNotifications: [
      {
        aiFeature: {
          featureId: '14',
          name: 'TICKET_TRIAGE',
          constant: 'TICKET_TRIAGE'
        },
        count: 15
      }
    ]
  }
};

let broadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

const useAINotificationHook = () => {
  const {
    initialized,
    setInitialized,
    notifications,
    setNotifications,
    count,
    setCount
  } = useAINotificationStore();

  const isTicketAuthorised = useIsAuthorisedHook('tickets.view');
  const aiFeatureHook = AIFeaturesHook();
  const { getIsAIFeatureEnabled } = aiFeatureHook;

  const isPatchAuthorised =
    useIsAuthorisedHook('patchViews.view') &&
    getIsAIFeatureEnabled('PATCH_AGENT');

  const AGENT_SOCKET_PERMISSION_MAP = {
    [AI_AGENTS_MAP.TICKET_TRIAGE]: isTicketAuthorised,
    [AI_AGENTS_MAP.PATCH_AGENT]: isPatchAuthorised
  };

  const getIsModulePermissionAuthorised = (module: string) => {
    return AGENT_SOCKET_PERMISSION_MAP[module];
  };

  broadcastChannel.onmessage = (wsMessage: any) => {
    if (wsMessage?.data && initialized) {
      const wsData = JSON.parse(wsMessage?.data);
      const payload = wsData?.payload;
      if (
        payload?.event === 'OVERLAY_UPDATE' &&
        payload?.data?.aiFeature?.constant
      ) {
        const messageData = payload?.data;
        const formattedMessageData = {
          ...messageData,
          count: messageData?.count || 1
        };
        const currentNotifications = notifications || [];
        const isNotificationExists = currentNotifications?.find(
          (el) =>
            el?.aiFeature?.constant ===
            formattedMessageData?.aiFeature?.constant
        );
        const isModulePermissionAuthroised = getIsModulePermissionAuthorised(
          messageData?.aiFeature?.constant
        );
        if (isModulePermissionAuthroised) {
          if (isNotificationExists) {
            const updatedNotifications = currentNotifications.map((el) => {
              if (
                el?.aiFeature?.constant ===
                formattedMessageData?.aiFeature?.constant
              ) {
                return {
                  ...el,
                  count: formattedMessageData?.count || 1
                };
              }
              return el;
            });
            setNotifications(updatedNotifications);
            setCount(
              updatedNotifications?.reduce((acc, el) => acc + el?.count, 0)
            );
          } else {
            const updatedNotifications = [
              ...currentNotifications,
              formattedMessageData
            ];
            setNotifications(updatedNotifications);
            setCount(
              updatedNotifications?.reduce((acc, el) => acc + el?.count, 0)
            );
          }
        }
      }
    }
  };

  const getInitialNotificationCount = async () => {
    const modules = [];
    if (isTicketAuthorised) {
      modules.push('TICKET');
    }
    if (isPatchAuthorised) {
      modules.push('PATCH');
    }

    if (!isEmpty(modules)) {
      const response = await AccountClient.query({
        query: GET_AI_AGENT_NOTIFICATIONS,
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            modules: modules
          }
        }
      });

      if (response?.data?.getAIAgentNotifications) {
        setInitialized(true);
        const notificationData =
          response?.data?.getAIAgentNotifications?.length > 0
            ? response?.data?.getAIAgentNotifications
            : null;
        setNotifications(notificationData);
        if (notificationData) {
          let totalCount = 0;
          notificationData?.forEach((el) => {
            totalCount += el?.count;
          });
          setCount(totalCount);
          return { count: totalCount, notifications: notificationData };
        }
        setCount(0);
        setNotifications(null);
        return { count: 0, notifications: null };
      }
    }
  };

  return {
    notifications,
    count,
    getInitialNotificationCount
  };
};

export default useAINotificationHook;
